package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.CoverageDto
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LClockIcon
import dev.lingvo.site.ds.LCloseIcon
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.attributes.type
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.files.File
import kotlin.random.Random

/** Computes grid navigation independently of DOM order, retaining one tab stop for the whole matrix. */
fun coverageFocusIndex(current: Int, rows: Int, columns: Int, key: String, ctrl: Boolean = false): Int {
    if (rows == 0 || columns == 0) return 0
    val last = rows * columns - 1
    val index = current.coerceIn(0, last)
    return when (key) {
        "ArrowLeft" -> (index - 1).coerceAtLeast(index / columns * columns)
        "ArrowRight" -> (index + 1).coerceAtMost(index / columns * columns + columns - 1)
        "ArrowUp" -> (index - columns).coerceAtLeast(index % columns)
        "ArrowDown" -> (index + columns).coerceAtMost((rows - 1) * columns + index % columns)
        "Home" -> if (ctrl) 0 else index / columns * columns
        "End" -> if (ctrl) last else index / columns * columns + columns - 1
        else -> index
    }
}

/** A single hover/focus description serves the whole matrix; statuses never depend on colour alone. */
@Composable
fun ShotCoverageGrid(
    coverage: CoverageDto, device: DeviceClass?, selected: ScreenshotsCell?, onOpen: (ScreenshotsCell, DeviceClass?) -> Unit,
    onFiles: (List<File>, ScreenshotsCell?, DeviceClass?) -> Unit = { _, _, _ -> }, canUpload: Boolean = false,
    refusals: Map<String, String> = emptyMap(), captureDates: Map<String, String> = emptyMap(),
) {
    val palette = ColorMode.current.toSitePalette()
    val uploadScope = rememberCoroutineScope()
    var fileFailure by remember { mutableStateOf<String?>(null) }
    val scope = remember { "coverage-${Random.nextInt(1_000_000)}" }
    val locales = remember(coverage.locales) { coverage.locales.sortedByDescending { it.isSource } }
    val rows = remember(coverage.rows) { coverage.rows.sortedBy { it.position } }
    var hasFocus by remember { mutableStateOf(false) }
    var focused by remember(device, rows.size, locales.size) { mutableStateOf(0) }
    var description by remember { mutableStateOf("") }
    var overflowing by remember { mutableStateOf(false) }
    val matrix = rows.flatMap { row -> locales.map { locale ->
        coverageGridCell(coverage, row.slotId, locale.locale, device, refusals)
    } }
    val summary = tf("screenshots.coverage.legend", "{current} current · {stale} stale · {missing} missing · {refused} refused · {notRequired} not required",
        "current" to matrix.count { it.status == ShotCellStatus.CURRENT }, "stale" to matrix.count { it.status == ShotCellStatus.STALE },
        "missing" to matrix.count { it.status == ShotCellStatus.MISSING }, "refused" to matrix.count { it.status == ShotCellStatus.REFUSED },
        "notRequired" to matrix.count { it.status == ShotCellStatus.NOT_REQUIRED })
    fileFailure?.let { LAlert(it, variant = LAlertVariant.WARNING) }
    Div(attrs = { attr("data-coverage-legend", "") }) { Text(summary) }
    if (overflowing) Div(attrs = { attr("data-coverage-scroll-hint", "") }) {
        Text(t("app.projects.slug.screenshots.coverage_grid.scroll_sideways_to_see_every_language", "Scroll sideways to see every language, or use the arrow keys in the grid."))
    }
    Div(Modifier.styleModifier { property("overflow", "auto"); property("width", "100%"); property("min-width", "0"); property("max-width", "100%"); property("position", "relative") }.toAttrs {
        attr("role", "region"); attr("aria-label", tr("app.projects.slug.screenshots.coverage_grid.screenshot_coverage_scroll_area", "Screenshot coverage scroll area"))
        ref { element ->
            val update = { overflowing = element.scrollWidth > element.clientWidth; element.tabIndex = if (overflowing) 0 else -1 }
            update()
            val callback: () -> Unit = { update() }
            val constructor = js("ResizeObserver")
            val resize = js("Reflect.construct(constructor, [callback])") // i18n-ignore
            resize.observe(element)
            onDispose { resize.disconnect() }
        }
        if (canUpload) {
            onDragOver { it.preventDefault() }
            onDrop { event ->
                event.preventDefault()
                collectDroppedCaptures(event.dataTransfer, uploadScope, { onFiles(it, null, device) }, { fileFailure = it })
            }
        }
    }) {
        Div(Modifier.styleModifier { property("display", "table"); property("table-layout", "fixed"); property("border-spacing", "${CoverageGridSizing.GAP}px")
            property("width", "100%"); property("min-width", "${CoverageGridSizing.minWidth(locales.size)}px")
            property("max-width", "${CoverageGridSizing.maxWidth(locales.size)}px"); property("margin-inline", "auto"); property("box-sizing", "border-box") }.toAttrs {
            attr("role", "grid"); attr("aria-label", trf("screenshots.coverage.label", "Screenshot coverage, {device}", "device" to
                (device?.displayName ?: tr("app.projects.slug.screenshots.coverage_grid.all_devices", "All devices"))))
            attr("aria-rowcount", (rows.size + 1).toString()); attr("aria-colcount", (locales.size + 1).toString())
        }) {
            Div(Modifier.styleModifier { property("display", "table-row") }.toAttrs { attr("role", "row"); attr("aria-rowindex", "1") }) {
                Div(Modifier.styleModifier { property("display", "table-cell"); property("width", "${CoverageGridSizing.SHOT_WIDTH}px"); property("position", "sticky"); property("inset-inline-start", "0"); property("background", palette.surface.toString()); property("z-index", "2") }.toAttrs { attr("role", "columnheader") }) { Text(t("app.projects.slug.screenshots.coverage_grid.shot", "Shot")) }
                locales.forEachIndexed { column, locale ->
                    // `normal`, not `anywhere`: `anywhere` lets the column shrink below one word, so on a phone "English" broke to "Englis" / "h".
                    Div(Modifier.monoFont().styleModifier { property("display", "table-cell"); property("text-align", "center"); property("overflow-wrap", "normal"); property("padding", "0 ${Space.SM}"); property("position", "sticky"); property("top", "0"); property("background", palette.surface.toString()) }.toAttrs {
                        attr("role", "columnheader"); attr("aria-colindex", (column + 2).toString()); attr("lang", locale.locale)
                    }) { Text(localeInfo(locale.locale).englishName) }
                }
            }
            rows.forEachIndexed { rowIndex, row ->
                Div(Modifier.styleModifier { property("display", "table-row") }.toAttrs { attr("role", "row"); attr("aria-rowindex", (rowIndex + 2).toString()) }) {
                    Div(Modifier.styleModifier {
                        property("display", "table-cell"); property("min-width", "${CoverageGridSizing.SHOT_WIDTH}px"); property("max-width", "${CoverageGridSizing.SHOT_WIDTH}px")
                        property("overflow-wrap", "anywhere"); property("position", "sticky"); property("inset-inline-start", "0")
                        property("background", palette.surface.toString()); property("z-index", "1")
                    }.toAttrs { attr("role", "rowheader"); attr("aria-colindex", "1") }) { Text(row.title) }
                    locales.forEachIndexed { column, locale ->
                        val index = rowIndex * locales.size + column
                        val cell = matrix[index]
                        val address = ScreenshotsCell(row.slotId, locale.locale)
                        val label = coverageCellLabel(row.title, locale.locale, cell, cell.capture?.captureId?.let { captureDates[it] })
                        val colour = when (cell.status) {
                            ShotCellStatus.CURRENT -> palette.success
                            ShotCellStatus.STALE -> palette.warning
                            ShotCellStatus.MISSING, ShotCellStatus.REFUSED -> palette.danger
                            ShotCellStatus.NOT_REQUIRED -> palette.borderStrong
                        }
                        Div(Modifier.styleModifier { property("display", "table-cell") }.toAttrs { attr("role", "gridcell"); attr("aria-colindex", (column + 2).toString()) }) {
                            Button(Modifier.styleModifier {
                                property("width", "100%"); property("height", "64px"); property("min-width", "40px"); property("box-sizing", "border-box")
                                property("display", "inline-flex"); property("align-items", "center"); property("justify-content", "center")
                                property("background", palette.surface.toString()); property("color", colour.toString())
                                property("border", "2px ${if (cell.status == ShotCellStatus.NOT_REQUIRED) "dashed" else "solid"} $colour")
                                property("border-radius", "8px"); property("cursor", "pointer")
                                if (selected == address || (hasFocus && focused == index)) { property("outline", "2px solid ${palette.accent}"); property("outline-offset", "2px") }
                            }.toAttrs {
                                id("$scope-$index"); type(ButtonType.Button); tabIndex(if (focused == index) 0 else -1)
                                attr("aria-label", label)
                                attr("data-shot-cell", "${row.slotId}:${locale.locale}")
                                onFocus { focused = index; description = label; hasFocus = true }
                                onBlur { hasFocus = false }
                                onMouseEnter { description = label }
                                onClick { onOpen(address, cell.device) }
                                onKeyDown { event ->
                                    if (event.key in listOf("ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End")) {
                                        event.preventDefault(); val rtl = kotlinx.browser.document.documentElement?.getAttribute("dir") == "rtl"
                                        val key = if (rtl && event.key == "ArrowLeft") "ArrowRight" else if (rtl && event.key == "ArrowRight") "ArrowLeft" else event.key
                                        focused = coverageFocusIndex(index, rows.size, locales.size, key, event.ctrlKey)
                                        (kotlinx.browser.document.getElementById("$scope-$focused") as? HTMLElement)?.focus()
                                    }
                                }
                                if (canUpload) {
                                    onDragOver { it.preventDefault(); it.stopPropagation() }
                                    onDrop { event ->
                                        event.preventDefault(); event.stopPropagation()
                                        collectDroppedCaptures(event.dataTransfer, uploadScope, { onFiles(it, address, cell.device) }, { fileFailure = it })
                                    }
                                    addEventListener("paste") { event ->
                                        val files = event.nativeEvent.asDynamic().clipboardData?.files
                                        if (files != null && (files.length as Int) > 0) {
                                            event.preventDefault()
                                            fileFailure = captureBatchLimitMessage(files.length as Int)
                                            if (fileFailure == null) onFiles((0 until (files.length as Int)).map { files.item(it) as File }, address, cell.device)
                                        }
                                    }
                                }
                            }) {
                                when (cell.status) {
                                    ShotCellStatus.CURRENT -> LCheckIcon(Modifier.iconSize(IconSize.SM))
                                    ShotCellStatus.STALE -> LClockIcon(Modifier.iconSize(IconSize.SM))
                                    ShotCellStatus.MISSING -> LCloseIcon(Modifier.iconSize(IconSize.SM))
                                    ShotCellStatus.REFUSED -> LWarningIcon(Modifier.iconSize(IconSize.SM))
                                    ShotCellStatus.NOT_REQUIRED -> Unit
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    Div(Modifier.toAttrs { id("$scope-description"); attr("data-coverage-description", "") }) { Text(description) }
}
